import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

export interface ScannedElement {
  testId: string;
  tagName: string;
  textPreview: string;
  className: string;
  rect: { top: number; left: number; width: number; height: number };
}

interface TestIdPickerDialogProps {
  open: boolean;
  onClose: () => void;
  pageUrl: string;
  onSelect: (testId: string) => void;
  onElementsFound?: (elements: ScannedElement[]) => void;
}

/**
 * Injects highlighting styles and click handlers into the iframe document
 * so that all `[data-testid]` elements are visually marked and clickable.
 */
function injectHighlighting(
  iframeDoc: Document,
  onSelect: (testId: string) => void,
  onElementsFound?: (elements: ScannedElement[]) => void
) {
  // Inject highlight styles
  const style = iframeDoc.createElement('style');
  style.textContent = `
    [data-testid] {
      outline: 2px dashed rgba(25, 118, 210, 0.5) !important;
      outline-offset: 2px !important;
      cursor: pointer !important;
      position: relative !important;
      transition: outline-color 0.15s, background-color 0.15s !important;
    }
    [data-testid]:hover {
      outline: 3px solid rgba(25, 118, 210, 0.9) !important;
      background-color: rgba(25, 118, 210, 0.08) !important;
    }
    .__testid-label {
      position: absolute;
      top: -22px;
      left: 0;
      z-index: 99999;
      background: #1976d2;
      color: #fff;
      font-size: 11px;
      font-family: monospace;
      padding: 2px 6px;
      border-radius: 3px;
      white-space: nowrap;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.15s;
      box-shadow: 0 1px 3px rgba(0,0,0,.3);
    }
    [data-testid]:hover > .__testid-label {
      opacity: 1;
    }
  `;
  iframeDoc.head.appendChild(style);

  const elements = iframeDoc.querySelectorAll('[data-testid]');
  const scanned: ScannedElement[] = [];

  elements.forEach((el) => {
    const testId = el.getAttribute('data-testid') || '';
    if (!testId) return;

    // Add floating label
    const label = iframeDoc.createElement('span');
    label.className = '__testid-label';
    label.textContent = testId;

    // Make sure parent is positioned
    const computed = iframeDoc.defaultView?.getComputedStyle(el);
    if (computed?.position === 'static') {
      (el as HTMLElement).style.position = 'relative';
    }
    el.appendChild(label);

    // Collect metadata
    const htmlEl = el as HTMLElement;
    const rect = htmlEl.getBoundingClientRect();
    const textContent = htmlEl.innerText || htmlEl.textContent || '';
    scanned.push({
      testId,
      tagName: el.tagName.toLowerCase(),
      textPreview: textContent.slice(0, 80).replace(/\s+/g, ' ').trim(),
      className: el.className
        .toString()
        .split(' ')
        .filter((c) => c && !c.startsWith('__'))
        .slice(0, 3)
        .join(' '),
      rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
    });

    // Click handler
    el.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      onSelect(testId);
    });
  });

  // De-duplicate by testId keeping the first occurrence
  const unique = scanned.filter(
    (item, idx, arr) => arr.findIndex((i) => i.testId === item.testId) === idx
  );

  onElementsFound?.(unique);
}

const TestIdPickerDialog: React.FC<TestIdPickerDialogProps> = ({
  open,
  onClose,
  pageUrl,
  onSelect,
  onElementsFound,
}) => {
  const { t } = useTranslation();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [elementCount, setElementCount] = useState(0);

  const handleIframeLoad = useCallback(() => {
    // Wait for React to render inside the iframe
    setTimeout(() => {
      try {
        const iframeDoc =
          iframeRef.current?.contentDocument ||
          iframeRef.current?.contentWindow?.document;
        if (!iframeDoc) return;

        injectHighlighting(
          iframeDoc,
          (testId) => {
            onSelect(testId);
            onClose();
          },
          (elements) => {
            setElementCount(elements.length);
            onElementsFound?.(elements);
          }
        );
      } catch (err) {
        console.error('[TestIdPicker] Could not access iframe:', err);
      } finally {
        setIsLoading(false);
      }
    }, 2500); // give React time to hydrate
  }, [onSelect, onClose, onElementsFound]);

  useEffect(() => {
    if (open) {
      setIsLoading(true);
      setElementCount(0);
    }
  }, [open, pageUrl]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '90vw',
          height: '85vh',
          maxWidth: '90vw',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1,
          px: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {t('help.pickTestId', 'Click on a highlighted element to select its data-testid')}
          </Typography>
          {!isLoading && elementCount > 0 && (
            <Chip
              label={`${elementCount} element${elementCount !== 1 ? 's' : ''}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, position: 'relative', flex: 1, overflow: 'hidden' }}>
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              backgroundColor: 'rgba(255,255,255,0.85)',
            }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('help.loadingPage', 'Loading page and scanning for data-testid attributes…')}
            </Typography>
          </Box>
        )}

        <iframe
          ref={iframeRef}
          src={pageUrl}
          onLoad={handleIframeLoad}
          title="Page preview for data-testid selection"
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default TestIdPickerDialog;

